'use client'

import {
  CheckCircle16,
  Clock16,
  Envelope16,
  LocationPin16,
  Umbrella16,
} from '@vinted/monochrome-icons'
import { Cell, Spacer } from '@vinted/web-ui'
import { InView } from 'react-intersection-observer'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'

import { itemPageViewEvent } from '@marketplace-web/item-page/item-page-data'

import { ItemPageUserInfoDetailsPluginModel } from './types'
import UserInfoLine from '../../components/UserInfoLine'
import useDate from '../../hooks/useDate'

type Props = {
  data: ItemPageUserInfoDetailsPluginModel
}

const getUserLocationDescription = (exposeLocation, city, countryTitleLocal) => {
  if (exposeLocation && city) {
    return `${city}, ${countryTitleLocal}`
  }

  return countryTitleLocal
}

const ItemPageUserInfoDetailsPlugin = ({ data }: Props) => {
  const translate = useTranslate()
  const { track } = useTracking()
  const { formatRelativeTime } = useDate()
  const isUKCompanyNumberHidden = useFeatureSwitch('hide_uk_sole_traders_company_number')

  const {
    sellerId,
    business,
    businessAccount,
    msgTemplateCount,
    isOnHoliday,
    lastLoggedOnTs,
    exposeLocation,
    city,
    countryTitleLocal,
  } = data
  if (!sellerId) return null

  const renderBusinessEmail = () => {
    if (!sellerId) return null

    const businessEmail = business && businessAccount?.email

    const hidePersonalInfo = business && businessAccount?.entity_type === 'sole_trader'

    if (!businessEmail) return null

    if (businessEmail && hidePersonalInfo) {
      return (
        <UserInfoLine iconName={Envelope16} truncate>
          {businessEmail}
        </UserInfoLine>
      )
    }

    return (
      <UserInfoLine iconName={Envelope16} truncate>
        <a href={`mailto:${businessEmail}`} title={businessEmail} data-testid="seller-email-link">
          {businessEmail}
        </a>
      </UserInfoLine>
    )
  }

  if (!sellerId) return null

  const businessLegalCode = business && businessAccount?.legal_code
  const hideBusinessLegalCode =
    isUKCompanyNumberHidden &&
    business &&
    businessAccount?.entity_type === 'sole_trader' &&
    businessAccount?.country === 'UK'

  const handleLastSeenView = (inView: boolean) => {
    if (!inView) return

    track(
      itemPageViewEvent({
        target: 'user_last_seen',
        item_id: data.itemId.toString(),
      }),
    )
  }

  return (
    <Cell>
      <SeparatedList separator={<Spacer size={Spacer.Size.Small} />}>
        <UserInfoLine iconName={LocationPin16}>
          {getUserLocationDescription(exposeLocation, city, countryTitleLocal)}
        </UserInfoLine>
        {renderBusinessEmail()}
        {businessLegalCode && !hideBusinessLegalCode && (
          <UserInfoLine iconName={CheckCircle16}>
            <span data-testid="seller-legal-code">
              {`${translate('user.verification.legal_code', {
                count: msgTemplateCount,
              })} ${businessLegalCode}`}
            </span>
          </UserInfoLine>
        )}
        {isOnHoliday && (
          <UserInfoLine iconName={Umbrella16}>
            <span>{translate('user.on_holiday')}</span>
          </UserInfoLine>
        )}
        {lastLoggedOnTs && (
          <InView onChange={handleLastSeenView}>
            <UserInfoLine iconName={Clock16}>
              <span suppressHydrationWarning>
                {`${translate('user.last_seen')} ${formatRelativeTime(lastLoggedOnTs)}`}
              </span>
            </UserInfoLine>
          </InView>
        )}
      </SeparatedList>
    </Cell>
  )
}

export default ItemPageUserInfoDetailsPlugin
